"use client";
import { useEffect, useState } from "react";
import MinimalReviewCards from "./MinimalReviewCards";
import Carrousel from "./Carrousel";
import ModernCarrousel from "./ModernCarrousel";
import MinimalCarrousel from "./MinimalCarrousel";

type GoogleReview = {
  author_name: string;
  author_url: string;
  language: string;
  profile_photo_url: string;
  rating: number;
  relative_time_description: string;
  text: string;
  time: number;
};

type Props = {
  header: string;
  subheader?: string;
  fallbackReviews: any[];
  display: string;
};

export default function GoogleFinancialPlanningReviewsSection({
  header,
  subheader,
  fallbackReviews,
  display,
}: Props) {
  const [reviews, setReviews] = useState<GoogleReview[] | undefined>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch("/api/reviews/google-financial-planning")
      .then((res) => res.json())
      .then((data) => {
        if (data.success && Array.isArray(data.reviews)) {
          const reviewsWithText = data.reviews.filter(
            (r: GoogleReview) => r.text && r.text.trim().length > 0
          );
          if (reviewsWithText.length > 0) {
            setReviews(reviewsWithText);
          } else {
            setReviews(undefined);
          }
        } else {
          setReviews(undefined);
        }
      })
      .catch(() => setReviews(undefined))
      .finally(() => setLoading(false));
  }, []);

  const mappedReviews = reviews !== undefined
    ? reviews.map((r, idx) => ({
        id: r.time + "-" + idx,
        review: {
          value: {
            schema: "dast",
            document: {
              type: "root",
              children: [
                {
                  type: "paragraph",
                  children: [{ type: "span", value: r.text }],
                },
              ],
            },
          },
        },
        reviewerName: r.author_name,
        reviewerTitle: r.relative_time_description,
        reviewerPicture: {
          responsiveImage: {
            src: r.profile_photo_url,
            width: 48,
            height: 48,
            alt: r.author_name,
          },
        },
      }))
    : fallbackReviews;

  if (display === "minimal_cards") {
    return <MinimalReviewCards header={header} subheader={subheader} reviews={mappedReviews} />;
  }
  if (display === "minimal_carrousel") {
    return <MinimalCarrousel header={header} subheader={subheader} reviews={mappedReviews} />;
  }
  if (display === "card_carrousel") {
    return <Carrousel header={header} subheader={subheader} reviews={mappedReviews} />;
  }
  if (display === "modern_carrousel") {
    return <ModernCarrousel header={header} subheader={subheader} reviews={mappedReviews} />;
  }

  return <MinimalReviewCards header={header} subheader={subheader} reviews={mappedReviews} />;
}
